/**
 * HTML Copy Formatters - Convert agent outputs to HTML for rich text clipboard
 */

import { formatLinkedInHTML } from './linkedinFormatterHTML';
import { formatTwitterHTML } from './twitterFormatterHTML';
import { formatNewsletterHTML } from './newsletterFormatterHTML';
import { formatPressReleaseHTML } from './pressReleaseFormatterHTML';
import { formatMarketingStrategistHTML } from './marketingStrategistFormatterHTML';
import { formatSEOKeywordHTML } from './seoKeywordFormatterHTML';
import { formatSEOContentHTML } from './seoContentFormatterHTML';

/**
 * Type definition for HTML copy formatter functions
 */
type HTMLCopyFormatter = (data: any) => string;

/**
 * Registry of HTML copy formatters by agent type
 */
const HTML_COPY_FORMATTERS: Record<string, HTMLCopyFormatter> = {
  linkedin_agent: formatLinkedInHTML,
  twitter_agent: formatTwitterHTML,
  newsletter_agent: formatNewsletterHTML,
  press_release_agent: formatPressReleaseHTML,
  marketing_strategist_agent: formatMarketingStrategistHTML,
  seo_keyword_agent: formatSEOKeywordHTML,
  seo_content_agent: formatSEOContentHTML,
};

/**
 * Get formatted HTML for copying based on agent type
 *
 * @param agentType - The type of agent that generated the content
 * @param agentOutput - The output data from the agent
 * @returns HTML string ready for rich text clipboard
 */
export const getCopyHTML = (agentType: string | undefined, agentOutput: any): string => {
  // If no agent type or output, return empty
  if (!agentOutput) {
    return '';
  }

  // If no agent type specified, return simple HTML
  if (!agentType) {
    return `<pre>${JSON.stringify(agentOutput, null, 2)}</pre>`;
  }

  // Get the appropriate formatter
  const formatter = HTML_COPY_FORMATTERS[agentType];

  // If no formatter found, fall back to JSON
  if (!formatter) {
    console.warn(`No HTML copy formatter found for agent type: ${agentType}`);
    return `<pre>${JSON.stringify(agentOutput, null, 2)}</pre>`;
  }

  // Use the formatter
  try {
    return formatter(agentOutput);
  } catch (error) {
    console.error(`Error formatting HTML copy for ${agentType}:`, error);
    // Fall back to JSON on error
    return `<pre>${JSON.stringify(agentOutput, null, 2)}</pre>`;
  }
};

// Export individual formatters for testing or direct use
export {
  formatLinkedInHTML,
  formatTwitterHTML,
  formatNewsletterHTML,
  formatPressReleaseHTML,
  formatMarketingStrategistHTML,
  formatSEOKeywordHTML,
  formatSEOContentHTML,
};

